<template>
    <div class="manifest-planning">
        <b-row>
            <b-col xl="5" class="mb-2 mb-xl-0">
                <section class="manifest-planning__section h-100">
                    <div class="mb-2">
                        <h5 class="mb-25">Planning policy</h5>
                        <div class="text-muted">
                            Choose how the import plan is grouped and executed.
                        </div>
                    </div>

                    <div v-if="policies.length" class="manifest-planning__policies">
                        <button
                            v-for="policy in policies"
                            :key="policyKey(policy)"
                            type="button"
                            class="manifest-planning__policy"
                            :class="{'manifest-planning__policy--selected': isSelected(policy)}"
                            :disabled="disabled"
                            @click="selectPolicy(policy)"
                        >
                            <span class="manifest-planning__policy-marker">
                                <feather-icon
                                    :icon="isSelected(policy) ? 'CheckIcon' : 'ShieldIcon'"
                                    size="18"
                                />
                            </span>
                            <span class="manifest-planning__policy-copy">
                                <span class="font-weight-bold">{{ policy.title }}</span>
                                <small class="text-muted">{{ policy.key }}@{{ policy.version }}</small>
                            </span>
                        </button>
                    </div>

                    <div v-else class="manifest-planning__empty text-muted">
                        The selected driver does not expose a planning policy.
                    </div>
                </section>
            </b-col>

            <b-col xl="7">
                <section class="manifest-planning__section h-100">
                    <template v-if="selectedPolicy">
                        <div class="d-flex align-items-start justify-content-between mb-2">
                            <div>
                                <div class="text-muted text-uppercase font-small-2 mb-25">
                                    Execution policy
                                </div>
                                <h4 class="mb-25">{{ selectedPolicy.title }}</h4>
                                <p class="text-muted mb-0">{{ selectedPolicy.description }}</p>
                            </div>
                            <b-badge variant="light-success">v{{ selectedPolicy.version }}</b-badge>
                        </div>

                        <div class="manifest-planning__identity mb-2">
                            <span class="text-muted">Policy identifier</span>
                            <code>{{ selectedPolicy.key }}@{{ selectedPolicy.version }}</code>
                        </div>

                        <div v-if="configurationArguments.length">
                            <h5 class="mb-25">Policy configuration</h5>
                            <p class="text-muted mb-1">
                                Configure the selected policy using its catalog schema.
                            </p>
                            <ManifestComponentArguments
                                :value="value.configuration"
                                :schema="argumentSchema"
                                :disabled="disabled"
                                @input="updateConfiguration"
                            />
                        </div>

                        <div v-else class="manifest-planning__fixed-configuration">
                            <feather-icon icon="CheckCircleIcon" size="18" class="mr-75" />
                            <div>
                                <div class="font-weight-bold">No additional configuration</div>
                                <small class="text-muted">
                                    This policy is ready to use with its catalog-defined behavior.
                                </small>
                            </div>
                        </div>
                    </template>

                    <div v-else class="manifest-planning__empty manifest-planning__empty--large text-muted">
                        <feather-icon icon="ShieldIcon" size="28" />
                        <div class="mt-1">Select a planning policy to continue.</div>
                    </div>
                </section>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {
    BBadge,
    BCol,
    BRow,
} from 'bootstrap-vue'
import ManifestComponentArguments from './ManifestComponentArguments.vue'
import {
    configurationArgumentSchema,
    initialConfiguration,
} from './manifestConfigurationSchema'

export default {
    name: 'ManifestPlanningStep',
    components: {
        BBadge,
        BCol,
        BRow,
        ManifestComponentArguments,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        policies: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        selectedPolicy() {
            return this.policies.find(policy => this.isSelected(policy)) || null
        },
        argumentSchema() {
            return configurationArgumentSchema(
                this.selectedPolicy ? this.selectedPolicy.configurationSchema : null
            )
        },
        configurationArguments() {
            return this.argumentSchema.arguments
        },
    },
    watch: {
        policies: {
            immediate: true,
            handler(policies) {
                this.selectOnlyPolicy(policies)
            },
        },
        disabled(disabled) {
            if (!disabled) {
                this.selectOnlyPolicy(this.policies)
            }
        },
    },
    methods: {
        policyKey(policy) {
            return `${policy.key}@${policy.version}`
        },
        isSelected(policy) {
            return Boolean(policy)
                && policy.key === this.value.policy
                && policy.version === this.value.policyVersion
        },
        selectOnlyPolicy(policies) {
            if (this.disabled || this.selectedPolicy || policies.length !== 1) {
                return
            }

            this.selectPolicy(policies[0])
        },
        selectPolicy(policy) {
            if (!policy || this.disabled || this.isSelected(policy)) {
                return
            }

            this.$emit('input', {
                policy: policy.key,
                policyVersion: policy.version,
                configuration: initialConfiguration(policy.configurationSchema),
            })
        },
        updateConfiguration(configuration) {
            if (this.disabled) {
                return
            }

            this.$emit('input', {
                ...this.value,
                configuration,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-planning__section {
    padding: 1.5rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.manifest-planning__policies {
    display: grid;
    gap: .75rem;
}

.manifest-planning__policy {
    display: flex;
    width: 100%;
    min-width: 0;
    align-items: center;
    padding: 1rem;
    border: 1px solid #d8d6de;
    border-radius: .357rem;
    color: inherit;
    background: #fff;
    text-align: left;
    transition: border-color .15s ease, background-color .15s ease;
}

.manifest-planning__policy:not(:disabled):hover,
.manifest-planning__policy--selected {
    border-color: var(--primary);
    background: rgba(0, 0, 0, .035);
}

.manifest-planning__policy-marker {
    display: flex;
    flex: 0 0 2.25rem;
    width: 2.25rem;
    height: 2.25rem;
    align-items: center;
    justify-content: center;
    margin-right: .75rem;
    border-radius: 50%;
    color: var(--primary);
    background: rgba(0, 0, 0, .045);
}

.manifest-planning__policy--selected .manifest-planning__policy-marker {
    color: #fff;
    background: var(--primary);
}

.manifest-planning__policy-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.manifest-planning__policy-copy small {
    overflow-wrap: anywhere;
}

.manifest-planning__identity,
.manifest-planning__fixed-configuration {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-planning__fixed-configuration {
    justify-content: flex-start;
    color: var(--success);
}

.manifest-planning__fixed-configuration small {
    color: var(--manifest-secondary-text-color, #45434c);
}

.manifest-planning__empty {
    padding: 1rem;
    border: 1px dashed #d8d6de;
    border-radius: .357rem;
    text-align: center;
}

.manifest-planning__empty--large {
    display: flex;
    min-height: 14rem;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}
</style>

